/*
  Collection infinite scroll with scroll position restore.

  Auto loads the next page when the bottom of the grid comes near, up to
  autoPages in a row, then shows a Load more button so the footer stays
  reachable. Clicking Load more resets the auto counter.

  Remembers how many pages were loaded and the scroll position, so returning
  from a product page rebuilds those pages and lands in the same spot.

  Theme notes:
  - Pagination arrow classes in this theme are mislabelled (the next arrow
    carries pagination__item--prev), so page numbers come from the numbered
    links instead.
  - Dawn's facets.js replaces #ProductGridContainer on filter or sort change,
    so a MutationObserver re-initialises after that.
  - Quick add buttons use delegation on document, so appended cards just work.
  - Detection is a throttled scroll listener rather than IntersectionObserver.
    IO proved unreliable in testing here and this is simpler to reason about.

  Loop safety: two things can ask for the next page, the scroll handler and
  the restore routine. If one is mid-request the other used to get an
  instantly resolved promise back and recurse without pause, which pinned the
  CPU. Busy now resolves after a delay, restore blocks the scroll handler
  while it runs, and restore gives up if a pass makes no progress.

  Scroll landing: the browser runs its own scroll restoration around the load
  event, which lands after the pages finish fetching and used to overwrite
  ours. scrollRestoration is set to manual before anything else, and the
  final position is reapplied a few times to survive images reflowing.

  After a restore the loader waits for a real scroll before auto loading
  again, so coming back from a product page does not immediately pull pages
  the shopper never asked for.

  The status line is screen reader only. Sighted shoppers can see products
  arrive, so on screen it was just noise, but it is the only signal a blind
  shopper gets that the page grew.
*/
(function () {
  var cfg = window.collectionInfiniteScrollConfig;
  if (!cfg || !cfg.enabled) return;

  var AUTO_PAGES = parseInt(cfg.autoPages, 10) || 3;
  var TRIGGER_DISTANCE = 700;
  var MAX_RESTORE = 12;
  var TTL = 30 * 60 * 1000;
  var KEY = 'tsp:collection-scroll';
  var SR_ONLY = 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';

  function viewKey() {
    var u = new URL(window.location.href);
    u.searchParams.delete('page');
    return u.pathname + u.search;
  }

  function readState() {
    try {
      var s = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (!s || s.key !== viewKey()) return null;
      if (Date.now() - s.at > TTL) return null;
      return s;
    } catch (e) {
      return null;
    }
  }

  function writeState(pages, y) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ key: viewKey(), pages: pages, y: y, at: Date.now() }));
    } catch (e) {}
  }

  function pageUrl(n) {
    var u = new URL(window.location.href);
    u.searchParams.set('page', n);
    return u.toString();
  }

  var pending = readState();
  if (pending && pending.pages > 1 && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  function Loader(container) {
    this.container = container;
    this.grid = container.querySelector('#product-grid');
    this.pagination = container.querySelector('.pagination-wrapper');
    this.page = 1;
    this.total = 1;
    this.autoLeft = AUTO_PAGES;
    this.busy = false;
    this.done = false;
    this.restoring = false;
    this.last = 0;
  }

  Loader.prototype.readTotal = function () {
    var max = 1;
    if (!this.pagination) return max;
    this.pagination.querySelectorAll('a[href*="page="]').forEach(function (a) {
      var m = a.getAttribute('href').match(/[?&]page=(\d+)/);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return max;
  };

  Loader.prototype.start = function () {
    if (!this.grid) return;
    this.total = this.readTotal();
    if (this.pagination) this.pagination.setAttribute('hidden', '');
    this.buildUi();
    if (this.total <= 1) {
      this.finish();
      return;
    }
    this.listen();
    this.trackExit();
  };

  Loader.prototype.buildUi = function () {
    var wrap = document.createElement('div');
    wrap.className = 'infinite-scroll';

    this.status = document.createElement('p');
    this.status.className = 'infinite-scroll__status';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.status.style.cssText = SR_ONLY;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'infinite-scroll__button';
    this.button.textContent = 'Load more';
    this.button.hidden = true;

    var self = this;
    this.button.addEventListener('click', function () {
      self.autoLeft = AUTO_PAGES;
      self.button.hidden = true;
      self.button.textContent = 'Load more';
      self.load().then(function () { self.check(); });
    });

    this.anchor = document.createElement('div');
    this.anchor.className = 'infinite-scroll__anchor';
    this.anchor.setAttribute('aria-hidden', 'true');

    wrap.appendChild(this.anchor);
    wrap.appendChild(this.button);
    wrap.appendChild(this.status);
    this.container.appendChild(wrap);
    this.ui = wrap;
  };

  Loader.prototype.nearBottom = function () {
    var rect = this.anchor.getBoundingClientRect();
    return rect.top - window.innerHeight < TRIGGER_DISTANCE;
  };

  Loader.prototype.check = function () {
    if (this.busy || this.done || this.restoring) return;
    if (!this.nearBottom()) return;
    if (this.autoLeft <= 0) {
      this.button.hidden = false;
      return;
    }
    var self = this;
    this.load().then(function () { self.check(); });
  };

  Loader.prototype.listen = function () {
    var self = this;
    var onScroll = function () {
      var now = Date.now();
      if (now - self.last < 120) return;
      self.last = now;
      self.check();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    this.detach = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    this.check();
  };

  Loader.prototype.load = function () {
    if (this.done) return Promise.resolve();
    if (this.busy) return new Promise(function (r) { setTimeout(r, 150); });
    if (this.page >= this.total) {
      this.finish();
      return Promise.resolve();
    }

    this.busy = true;
    var next = this.page + 1;
    var self = this;

    return fetch(pageUrl(next), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Request failed with status ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('#product-grid .grid__item');
        if (!items.length) {
          self.finish();
          return;
        }
        items.forEach(function (li) {
          self.grid.appendChild(document.importNode(li, true));
        });
        self.page = next;
        self.autoLeft -= 1;
        self.status.textContent = items.length + ' more products loaded';
        if (!self.restoring) writeState(self.page, window.scrollY);
        if (self.page >= self.total) self.finish();
        else if (self.autoLeft <= 0 && !self.restoring) self.button.hidden = false;
      })
      .catch(function (err) {
        console.error('[infinite-scroll]', err);
        self.status.textContent = 'Could not load more products.';
        self.button.textContent = 'Try again';
        self.button.hidden = false;
      })
      .then(function () {
        self.busy = false;
      });
  };

  Loader.prototype.finish = function () {
    this.done = true;
    this.restoring = false;
    if (this.detach) this.detach();
    if (this.button) this.button.hidden = true;
    if (this.status) this.status.textContent = '';
  };

  Loader.prototype.trackExit = function () {
    var self = this;
    var save = function () { writeState(self.page, window.scrollY); };
    this.grid.addEventListener('click', function (e) {
      if (e.target.closest('a[href]')) save();
    });
    window.addEventListener('pagehide', save);
  };

  Loader.prototype.restore = function (state) {
    var self = this;
    var target = Math.min(state.pages, this.total, MAX_RESTORE);

    var settle = function () {
      var apply = function () { window.scrollTo(0, state.y); };
      apply();
      if (window.requestAnimationFrame) requestAnimationFrame(apply);
      setTimeout(apply, 80);
      setTimeout(apply, 300);
      setTimeout(function () {
        apply();
        writeState(self.page, state.y);
      }, 800);
    };

    var land = function () {
      self.restoring = false;
      self.status.textContent = '';
      self.autoLeft = AUTO_PAGES;
      if (self.page < self.total) self.button.hidden = false;
      settle();
    };

    if (target <= 1) {
      settle();
      return;
    }

    this.restoring = true;
    this.status.textContent = 'Returning you to where you left off';

    var step = function () {
      if (self.done || self.page >= target) return land();
      var before = self.page;
      self.load().then(function () {
        if (self.page === before) return land();
        step();
      });
    };

    step();
  };

  var current = null;

  function init() {
    var container = document.querySelector('#ProductGridContainer');
    if (!container || !container.querySelector('#product-grid')) return;
    if (current) current.finish();

    var old = container.querySelector('.infinite-scroll');
    if (old) old.remove();

    current = new Loader(container);
    window.tspInfiniteScroll = current;
    current.start();

    var state = readState();
    if (state && state.pages > 1 && !current.done) {
      current.restore(state);
    }
  }

  function watchFacets() {
    var host = document.querySelector('#ProductGridContainer');
    if (!host) return;
    var seen = host.querySelector('#product-grid');
    new MutationObserver(function () {
      var grid = host.querySelector('#product-grid');
      if (grid && grid !== seen) {
        seen = grid;
        try { sessionStorage.removeItem(KEY); } catch (e) {}
        init();
      }
    }).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); watchFacets(); });
  } else {
    init();
    watchFacets();
  }
})();
